import i18n, { t } from 'i18next'

import Status from '~/components/Status'
import { Badge } from '~/components/UI/Badge'
import { getRetailMediaAccountType, isReadOnly } from '~/helpers'
import { ConversionRateCutoffAlertIcon } from '~/modules/retailMedia/components/ConversionRateCutoffAlertIcon'
import { ConversionsCutoffAlertIcon } from '~/modules/retailMedia/components/ConversionsCutoffAlertIcon'
import { RoasMetricCell } from '~/modules/retailMedia/components/RoasMetricCell'
import { formatRoasCutoffDate } from '~/modules/retailMedia/constants'
import { campaignStatusBadge } from '~/modules/retailMedia/helpers/campaignStatusBadge'
import {
  buildNtbColumns,
  buildNtbTooltipValues
} from '~/modules/retailMedia/helpers/ntbMetrics'

import { accountInfo } from '../../_elements/accountInfo'
import { ImpressionShareMetricCell } from '../../_elements/ImpressionShareMetricCell'
import {
  haloColumns,
  buildClickAttributionColumns
} from '../../_elements/roasColumns'
import { MetricTooltip } from '../../../MetricMethodologyTooltip'
import { statusType } from '../../../StatusBar/statusType'
import ToogleActiveCampaign from '../../../ToogleActiveCampaign'
import BudgetCell from './elements/BudgetCell'
import TitleWithStatus from './elements/TitleWithStatus'

type SchemaProps = {
  isListAll?: boolean
  insideCampaign?: boolean
  campaignAdType?: CampaignAdType
  availableMetrics?: NewMetricsAvailability
  showClickAlert?: boolean
  lookbackMonths?: number
  ntbOrdersWindow?: NtbOrdersWindow
}

export const schema = ({
  isListAll,
  availableMetrics = {
    impressionShare: false,
    lostIsRank: false,
    lostIsBudget: false,
    newToBrandCustomers: false,
    newToBrandRevenue: false
  },
  showClickAlert = false,
  lookbackMonths,
  ntbOrdersWindow
}: SchemaProps) => {
  /**
   * Colunas de atribuição (Halo + ROAS por clique) são compartilhadas e tipadas
   * para AdsResultFormatted. Adaptamos uma vez ao tipo da tabela de campanhas
   * para podermos referenciá-las individualmente na ordem desejada.
   */
  const attributionColumns = {
    ...haloColumns,
    ...buildClickAttributionColumns(showClickAlert)
  } as unknown as TableSchema<FormatListCampaign>

  const accountType = getRetailMediaAccountType()

  return {
    /**
     * Informações da campanha
     */
    campaingInfo: {
      title: t('rm:Campaigns'),
      sortKey: 'name',
      render: rowData => {
        const isObjectiveBased = rowData.raw.strategy_type === 'objective_based'
        const goal = rowData.raw.settings.goal
        const target = rowData?.raw?.target

        return (
          <div style={{ display: 'flex', gap: '.5rem', alignItems: 'center' }}>
            {/* Toggle para gerenciar atividade da campanha. Apenas advertiser */}
            {accountType === 'advertiser' && !isReadOnly() && (
              <aside>
                <ToogleActiveCampaign
                  data={{ ...rowData, target }}
                  key={rowData.id}
                  isList
                />
              </aside>
            )}

            <TitleWithStatus
              title={rowData.title}
              linkTo={`/retail-media/campaign/view/${rowData.id}`}
              statusColor={statusType[rowData.status]?.color}
              isObjectiveBased={isObjectiveBased}
              goal={goal}
            />
          </div>
        )
      },

      showTotal: true
    },
    /**
     * Informações da conta vinculada se estiver fora da campanha
     */
    ...(isListAll ? { ...accountInfo() } : {}),
    type: {
      title: t('rm:Type'),
      sortKey: 'ad_type',
      render: rowData => (
        <>
          <div style={{ lineHeight: 1, fontSize: '.75rem' }}>
            {rowData.adType}
          </div>
          {rowData?.targetingType && (
            <Badge size="tiny" text={t(`rm:${rowData.targetingType}`)} />
          )}
        </>
      ),
      className: 'order__details col__infos col__title'
    },
    status: {
      title: t('rm:Status'),
      sortKey: 'status',
      render: rowData => (
        <div style={{ lineHeight: 1, fontSize: '.75rem' }}>
          <Status status={campaignStatusBadge(rowData.status)} />
        </div>
      ),
      className: 'order__status'
    },
    budget: {
      title: t('rm:dailyBudget'),
      sortInitialToggleDirection: 'DESC',
      sortKey: 'daily_budget',
      render: ({ budget, raw, id }) => (
        <BudgetCell budget={budget} raw={raw} id={id} />
      )
    },
    prints: {
      title: t('rm:metrics.impressions'),
      helpText: t('rm:metrics.impressions.tooltip'),
      sortKey: 'impressions',
      sortInitialToggleDirection: 'DESC',
      render: ({ metrics }) => metrics?.impressions
    },
    views: {
      title: t('rm:metrics.views'),
      helpText: t('rm:metrics.views.tooltip'),
      sortKey: 'views',
      sortInitialToggleDirection: 'DESC',
      render: ({ metrics }) => metrics?.views
    },
    clicks: {
      title: t('rm:metrics.clicks'),
      helpText: t('rm:metrics.clicks.tooltip'),
      sortKey: 'clicks',
      sortInitialToggleDirection: 'DESC',
      render: ({ metrics }) => metrics?.clicks
    },
    ctr: {
      title: t('rm:metrics.ctr'),
      helpContent: <MetricTooltip metric="ctr" />,
      sortKey: 'ctr',
      sortInitialToggleDirection: 'DESC',
      render: ({ metrics }) => metrics.ctr,
      optional: true
    },
    totalSpent: {
      title:
        accountType === 'publisher'
          ? t('rm:metrics.adsRevenue')
          : t('rm:metrics.investment'),
      helpText:
        accountType === 'publisher'
          ? t('rm:metrics.adsRevenue.tooltip')
          : t('rm:metrics.investment.tooltip'),
      sortKey: 'total_spent',
      sortInitialToggleDirection: 'DESC',
      render: ({ metrics }) => metrics?.totalSpent
    },
    conversions: {
      title: t('rm:metrics.conversions'),
      headerSuffix: showClickAlert ? <ConversionsCutoffAlertIcon /> : undefined,
      helpText: t('rm:metrics.conversions.tooltip'),
      sortKey: 'conversions',
      sortInitialToggleDirection: 'DESC',
      render: ({ metrics }) => metrics?.conversions
    },
    conversionsClick: {
      ...attributionColumns.conversionsClick,
      optional: true,
      helpContent: <MetricTooltip metric="conversionsClick" />
    },
    conversion_rate: {
      title: t('rm:metrics.conversion_rate'),
      headerSuffix: showClickAlert ? (
        <ConversionRateCutoffAlertIcon />
      ) : undefined,
      helpContent: (
        <MetricTooltip
          metric="conversion_rate"
          values={{ date: formatRoasCutoffDate(i18n.language) }}
          learnMoreHref={t('rm:alerts.conversionUpdate.link')}
        />
      ),
      sortKey: 'conversion_rate',
      sortInitialToggleDirection: 'DESC',
      render: ({ metrics }) => metrics?.conversion_rate
    },
    conversionRateClick: {
      ...attributionColumns.conversionRateClick,
      optional: true,
      helpContent: <MetricTooltip metric="conversionRateClick" />
    },
    income: {
      title: t('rm:metrics.sales'),
      helpText: t('rm:metrics.sales.tooltip'),
      sortKey: 'income',
      sortInitialToggleDirection: 'DESC',
      render: ({ metrics }) => metrics?.income
    },
    salesClick: {
      ...attributionColumns.salesClick,
      optional: true,
      helpText: t('rm:metrics.salesClick.tooltip')
    },
    roas: {
      title: t('rm:metrics.roas'),
      helpContent: (
        <MetricTooltip
          metric="roas"
          values={{ date: formatRoasCutoffDate(i18n.language) }}
          learnMoreHref={t('rm:alerts.roasUpdate.link')}
        />
      ),
      sortKey: 'roas',
      sortInitialToggleDirection: 'DESC',
      render: ({ metrics }) => (
        <RoasMetricCell
          metric="roas"
          value={metrics?.roas}
          raw={metrics?.raw}
        />
      )
    },
    roasClick: {
      ...attributionColumns.roasClick,
      optional: true,
      helpContent: <MetricTooltip metric="roasClick" />
    },
    roasHalo: {
      ...attributionColumns.roasHalo,
      optional: true,
      helpContent: <MetricTooltip metric="roasHalo" />
    },
    roasOverall: {
      ...attributionColumns.roasOverall,
      optional: true,
      helpContent: <MetricTooltip metric="roasOverall" />
    },
    haloOrders: {
      ...attributionColumns.haloOrders,
      optional: true,
      helpContent: <MetricTooltip metric="haloOrders" />
    },
    haloRevenue: {
      ...attributionColumns.haloRevenue,
      optional: true,
      helpContent: <MetricTooltip metric="haloRevenue" />
    },
    haloItems: {
      ...attributionColumns.haloItems,
      optional: true,
      helpContent: <MetricTooltip metric="haloItems" />
    },
    /* eslint-disable indent */
    ...(availableMetrics.impressionShare
      ? {
          impressionShare: {
            title: t('rm:metrics.impressionShare'),
            helpContent: <MetricTooltip metric="impressionShare" />,
            helpText: t('rm:metrics.impressionShare.tooltip'),
            sortKey: 'impression_share',
            sortInitialToggleDirection: 'DESC',
            optional: true,
            render: ({ metrics }) => (
              <ImpressionShareMetricCell
                value={metrics?.impressionShare}
                raw={metrics?.raw}
              />
            )
          }
        }
      : {}),
    ...(availableMetrics.lostIsRank
      ? {
          lostIsRank: {
            title: t('rm:metrics.lostIsRank'),
            helpContent: <MetricTooltip metric="lostIsRank" />,
            helpText: t('rm:metrics.lostIsRank.tooltip'),
            sortKey: 'lost_is_rank',
            sortInitialToggleDirection: 'DESC',
            optional: true,
            render: ({ metrics }) => (
              <ImpressionShareMetricCell
                value={metrics?.lostIsRank}
                raw={metrics?.raw}
              />
            )
          }
        }
      : {}),
    ...(availableMetrics.lostIsBudget
      ? {
          lostIsBudget: {
            title: t('rm:metrics.lostIsBudget'),
            helpContent: <MetricTooltip metric="lostIsBudget" />,
            helpText: t('rm:metrics.lostIsBudget.tooltip'),
            sortKey: 'lost_is_budget',
            sortInitialToggleDirection: 'DESC',
            optional: true,
            render: ({ metrics }) => (
              <ImpressionShareMetricCell
                value={metrics?.lostIsBudget}
                raw={metrics?.raw}
              />
            )
          }
        }
      : {}),
    ...buildNtbColumns<FormatListCampaign>({
      availableMetrics,
      ntbValues: buildNtbTooltipValues({
        lookbackMonths,
        ordersWindow: ntbOrdersWindow
      }),
      render:
        metric =>
        ({ metrics }) =>
          metrics?.[metric.key]
    })
    /* eslint-enable indent */
  } satisfies TableSchema<FormatListCampaign>
}
